import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import debounce from 'lodash.debounce';
import { toast, ToastContainer } from 'react-toastify';
import Swal from 'sweetalert2';
import { BASE_URL } from '../../api';

const API_BASE = `${BASE_URL}/api`;
const ROUTE_API = `${API_BASE}/routemasters`;
const MASTER_API = `${API_BASE}/master`;

const AddRoute = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routeToEdit = location.state?.route;

  // ------------------------------------------------------------------ STATE
  const [vehicles, setVehicles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showHubForm, setShowHubForm] = useState(false);
  const [newHub, setNewHub] = useState({ code: '', name: '' });

  const [states, setStates] = useState([]);
  const [originAreas, setOriginAreas] = useState([]);
  const [destinationAreas, setDestinationAreas] = useState([]);

  const [pendingStates, setPendingStates] = useState({ origin: null, destination: null });

  const [formData, setFormData] = useState({
    _id: null,
    originstate: '',
    originStatename: '',
    destinationstate: '',
    destinationStatename: '',
    originid: '',
    originname: '',
    destinationid: '',
    destinationname: '',
    routedate: '',
    routetiming: '',
    modeid: '',
    transportid: '',
    vehicletypeid: '',
    vehiclenumber: '',
    vehiclenumberText: '',
    drivername: '',
    hub: '',
    cocourier: 0,
    coloader: 0,
    statusid: true,
  });

  const [hubs, setHubs] = useState(() => {
    const saved = localStorage.getItem('hubs');
    return saved
      ? JSON.parse(saved)
      : [
        { code: 'MAA', name: 'CHENNAI', id: 1 },
        { code: 'VPM', name: 'VILLUPURAM', id: 2 },
        { code: 'TRY', name: 'TRICHY', id: 3 },
        { code: 'MDR', name: 'MADURAI', id: 4 },
        { code: 'TVL', name: 'TIRUNELVELI', id: 5 },
      ];
  });

  // ------------------------------------------------------------------ AUTH
  const token = localStorage.getItem('token') || '';
  const getHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  });

  const checkToken = () => {
    if (!token) {
      toast.error('Please login again');
      navigate('/login');
      return false;
    }
    return true;
  };

  // ------------------------------------------------------------------ FETCH HELPERS
  const fetchStates = async () => {
    if (!checkToken()) return;
    try {
      const r = await fetch(`${MASTER_API}/state`, { headers: getHeaders() });
      const j = await r.json();
      if (j.success) {
        const opts = j.data.map(s => ({ value: s._id, label: s.statename }));
        setStates(opts);
      }
    } catch (e) {
      toast.error('Failed to load states');
    }
  };

  /**
   * UPDATED: fetchAreas now extracts the nested pincode object 
   * and formats the label as "AREANAME - PINCODE"
   */
  const fetchAreas = async (stateId) => {
    if (!stateId) return [];
    try {
      const r = await fetch(`${ROUTE_API}/getByState`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ stateid: stateId }),
      });
      const j = await r.json();
      return (j.data || []).map(a => ({
        value: a._id,
        // Combined Label logic
        label: `${a.areaname?.trim() || ''} - ${a.pincode?.pincode || 'N/A'}`,
      }));
    } catch (e) {
      toast.error('Failed to fetch areas' || 'Error');
      return [];
    }
  };

  const fetchVehicles = async (input = '') => {
    if (!checkToken()) return [];
    try {
      const url = input
        ? `${MASTER_API}/transport?search=${encodeURIComponent(input)}`
        : `${MASTER_API}/transport`;
      const r = await fetch(url, { headers: getHeaders() });
      const j = await r.json();
      if (j.success && Array.isArray(j.data)) {
        const opts = j.data.map(v => ({
          value: v._id,
          label: v.vehicleno,
          vehicleno: v.vehicleno,
          vehicletypeid: v.vehicletypeid,
          ownername: v.ownername,
          id: v._id,
        }));
        setVehicles(j.data);
        return opts;
      }
    } catch (e) {
      toast.error('Failed to load vehicles');
    }
    return [];
  };

  const loadVehicleOptions = useCallback(
    debounce((input, cb) => fetchVehicles(input).then(cb), 500),
    [token]
  );

  // ------------------------------------------------------------------ EFFECTS
  useEffect(() => {
    fetchStates();
    fetchVehicles();
  }, []);

  useEffect(() => {
    if (formData.originstate) {
      fetchAreas(formData.originstate).then(setOriginAreas);
    } else {
      setOriginAreas([]);
      setFormData(p => ({ ...p, originid: '', originname: '' }));
    }
  }, [formData.originstate]);

  useEffect(() => {
    if (formData.destinationstate) {
      fetchAreas(formData.destinationstate).then(setDestinationAreas);
    } else {
      setDestinationAreas([]);
      setFormData(p => ({ ...p, destinationid: '', destinationname: '' }));
    }
  }, [formData.destinationstate]);

  // ------------------------------ EDIT MODE PRE-FILL
  useEffect(() => {
    if (!routeToEdit) return;

    const originObj = routeToEdit.originid || {};
    const destObj = routeToEdit.destinationid || {};

    setPendingStates({
      origin: {
        id: routeToEdit.originstate || '',
        name: routeToEdit.originStatename || '',
      },
      destination: {
        id: routeToEdit.destinationstate || '',
        name: routeToEdit.destinationStatename || '',
      },
    });

    setFormData({
      _id: routeToEdit._id || null,
      originstate: '',
      originStatename: '',
      destinationstate: '',
      destinationStatename: '',
      originid: originObj._id || '',
      // Ensure existing data also shows the pincode format in edit mode
      originname: originObj.areaname
        ? `${originObj.areaname} - ${originObj.pincode?.pincode || ''}`.trim()
        : '',
      destinationid: destObj._id || '',
      destinationname: destObj.areaname
        ? `${destObj.areaname} - ${destObj.pincode?.pincode || ''}`.trim()
        : '',
      routedate: routeToEdit.routedate
        ? new Date(routeToEdit.routedate).toISOString().split('T')[0]
        : '',
      routetiming: routeToEdit.routetiming || '',
      modeid: String(routeToEdit.modeid || ''),
      transportid: routeToEdit.transportid?._id || '',
      vehicletypeid: String(routeToEdit.vehicletypeid || ''),
      vehiclenumber: routeToEdit.vehiclenumber || '',
      vehiclenumberText: routeToEdit.vehiclenumberText || '',
      drivername: routeToEdit.drivername || '',
      hub: routeToEdit.hub || '',
      cocourier: routeToEdit.cocourier ?? 0,
      coloader: routeToEdit.coloader ?? 0,
      statusid: routeToEdit.statusid ?? true,
    });
  }, [routeToEdit]);

  useEffect(() => {
    if (!pendingStates.origin && !pendingStates.destination) return;
    if (states.length === 0) return;

    if (pendingStates.origin?.id) {
      const match = states.find(s => s.value === pendingStates.origin.id);
      if (match) {
        handleChange('originstate', pendingStates.origin.id, pendingStates.origin.name);
      }
    }

    if (pendingStates.destination?.id) {
      const match = states.find(s => s.value === pendingStates.destination.id);
      if (match) {
        handleChange('destinationstate', pendingStates.destination.id, pendingStates.destination.name);
      }
    }

    setPendingStates({ origin: null, destination: null });
  }, [states, pendingStates]);

  useEffect(() => {
    localStorage.setItem('hubs', JSON.stringify(hubs));
  }, [hubs]);

  // ------------------------------------------------------------------ HANDLERS
  const handleChange = (name, value, label = '') => {
    if (name === 'vehiclenumber') {
      const v = vehicles.find(x => x._id === value);
      setFormData(p => ({
        ...p,
        vehiclenumber: value || '',
        vehiclenumberText: v?.vehicleno || label || '',
        vehicletypeid: String(v?.vehicletypeid || ''),
        drivername: v?.ownername || '',
        transportid: v?._id?.toString() || '',
      }));
    } else if (name === 'originstate') {
      setFormData(p => ({
        ...p,
        originstate: value || '',
        originStatename: label || '',
        originid: '',
        originname: '',
      }));
    } else if (name === 'destinationstate') {
      setFormData(p => ({
        ...p,
        destinationstate: value || '',
        destinationStatename: label || '',
        destinationid: '',
        destinationname: '',
      }));
    } else if (name === 'originid') {
      setFormData(p => ({ ...p, originid: value, originname: label }));
    } else if (name === 'destinationid') {
      setFormData(p => ({ ...p, destinationid: value, destinationname: label }));
    } else {
      setFormData(p => ({ ...p, [name]: value }));
    }
  };

  const handleHubCheck = e => {
    const { value, checked } = e.target;
    let list = formData.hub ? formData.hub.split(',').map(h => h.trim()) : [];
    if (checked && !list.includes(value)) {
      list.push(value);
    } else {
      list = list.filter(h => h !== value);
    }
    setFormData(p => ({ ...p, hub: list.join(', ') }));
  };

  const addHub = () => {
    if (!newHub.code || !newHub.name) return toast.error('Both fields required');
    if (hubs.some(h => h.code === newHub.code)) return toast.error('Code exists');
    setHubs(p => [...p, { ...newHub, id: p.length + 1 }]);
    setNewHub({ code: '', name: '' });
    setShowHubForm(false);
    toast.success('Hub added');
  };

  // ------------------------------------------------------------------ SUBMIT
  const handleSubmit = async e => {
    e.preventDefault();
    if (!checkToken()) return;

    const required = [
      'originstate', 'destinationstate', 'originid', 'destinationid',
      'routedate', 'routetiming', 'modeid', 'transportid',
      'vehiclenumber', 'vehiclenumberText', 'drivername',
    ];
    const missing = required.filter(f => !formData[f]);
    if (missing.length) {
      toast.error(`Missing: ${missing.join(', ')}`);
      return;
    }
    if (formData.originid === formData.destinationid) {
      toast.error('Origin and Destination cannot be the same');
      return;
    }

    setIsSubmitting(true);

    const payload = {
      ...(formData._id ? { _id: formData._id } : {}),
      originstate: formData.originstate,
      originStatename: formData.originStatename,
      destinationstate: formData.destinationstate,
      destinationStatename: formData.destinationStatename,
      originid: formData.originid,
      destinationid: formData.destinationid,
      routedate: formData.routedate,
      routetiming: formData.routetiming,
      modeid: parseInt(formData.modeid, 10),
      transportid: formData.transportid,
      vehicletypeid: parseInt(formData.vehicletypeid, 10) || 0,
      vehiclenumber: formData.vehiclenumber,
      vehiclenumberText: formData.vehiclenumberText,
      drivername: formData.drivername,
      hub: formData.hub,
      cocourier: formData.cocourier,
      coloader: formData.coloader,
      statusid: formData.statusid,
      operationflag: formData._id ? 'UPDATE' : 'INSERT',
    };

    try {
      const url = formData._id ? `${ROUTE_API}/${formData._id}` : ROUTE_API;
      const method = formData._id ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (json.success) {
        toast.success(`Route ${formData._id ? 'updated' : 'added'}`);
        navigate('/Routepage');
      } else {
        throw new Error(json.message || 'Save failed');
      }
    } catch (err) {
      toast.error(err.message || 'Network error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="container mx-auto mt-10 px-4">
      <ToastContainer />
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">
            {formData._id ? 'Edit Route' : 'Add Route'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6 bg-white p-8 rounded-lg shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

              {/* Origin State */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Origin State *</label>
                <Select
                  options={states}
                  value={formData.originstate ? { value: formData.originstate, label: formData.originStatename } : null}
                  onChange={opt => handleChange('originstate', opt?.value, opt?.label)}
                  placeholder="Select Origin State"
                  isClearable
                  isDisabled={isSubmitting}
                />
              </div>

              {/* Destination State */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Destination State *</label>
                <Select
                  options={states}
                  value={formData.destinationstate ? { value: formData.destinationstate, label: formData.destinationStatename } : null}
                  onChange={opt => handleChange('destinationstate', opt?.value, opt?.label)}
                  placeholder="Select Destination State"
                  isClearable
                  isDisabled={isSubmitting}
                />
              </div>

              {/* Origin Area with Pincode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Origin *</label>
                <Select
                  options={originAreas}
                  value={formData.originid ? { value: formData.originid, label: formData.originname } : null}
                  onChange={opt => handleChange('originid', opt?.value, opt?.label)}
                  placeholder="Select Origin"
                  isDisabled={!formData.originstate || isSubmitting}
                  isClearable
                />
              </div>

              {/* Destination Area with Pincode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Destination *</label>
                <Select
                  options={destinationAreas}
                  value={formData.destinationid ? { value: formData.destinationid, label: formData.destinationname } : null}
                  onChange={opt => handleChange('destinationid', opt?.value, opt?.label)}
                  placeholder="Select Destination"
                  isDisabled={!formData.destinationstate || isSubmitting}
                  isClearable
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input
                  type="date"
                  className="mt-1 p-2 border rounded w-full text-sm"
                  value={formData.routedate}
                  onChange={e => handleChange('routedate', e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time *</label>
                <input
                  type="time"
                  className="mt-1 p-2 border rounded w-full text-sm"
                  value={formData.routetiming}
                  onChange={e => handleChange('routetiming', e.target.value)}
                  required
                  disabled={isSubmitting}
                />
              </div>

              {/* Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mode *</label>
                <select
                  className="mt-1 p-2 border rounded w-full text-sm"
                  value={formData.modeid}
                  onChange={e => handleChange('modeid', e.target.value)}
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Select Mode</option>
                  <option value="1">Air</option>
                  <option value="2">Surface</option>
                  <option value="3">Ship</option>
                </select>
              </div>

              {/* Vehicle No */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle No *</label>
                <AsyncSelect
                  cacheOptions
                  loadOptions={loadVehicleOptions}
                  defaultOptions
                  value={formData.vehiclenumber ? { value: formData.vehiclenumber, label: formData.vehiclenumberText } : null}
                  onChange={opt => handleChange('vehiclenumber', opt?.value, opt?.label)}
                  placeholder="Search Vehicle..."
                  isClearable
                  isDisabled={isSubmitting}
                />
              </div>

              {/* Vehicle Type (Auto-filled) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type</label>
                <input
                  type="text"
                  className="mt-1 p-2 border rounded w-full text-sm bg-gray-100"
                  value={
                    formData.vehicletypeid === '1' ? 'Truck' :
                      formData.vehicletypeid === '2' ? 'Tempo' :
                        formData.vehicletypeid === '3' ? 'Lorry' : ''
                  }
                  readOnly
                />
              </div>

              {/* Driver Name (Auto-filled) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Driver Name</label>
                <input
                  type="text"
                  className="mt-1 p-2 border rounded w-full text-sm bg-gray-100"
                  value={formData.drivername}
                  readOnly
                />
              </div>
            </div>

            {/* Hub Section */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Hub</label>
              <textarea
                className="w-full p-2 border rounded bg-gray-100"
                value={formData.hub}
                readOnly
                rows={3}
              />
              <button
                type="button"
                onClick={() => setShowHubForm(true)}
                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded text-sm"
                disabled={isSubmitting}
              >
                Add New Hub
              </button>

              <div className="mt-4 max-h-40 overflow-y-auto">
                {hubs.map(h => (
                  <div key={h.id} className="flex items-center mb-1">
                    <input
                      type="checkbox"
                      id={`hub-${h.id}`}
                      value={h.code}
                      checked={formData.hub.includes(h.code)}
                      onChange={handleHubCheck}
                      className="h-4 w-4 text-blue-600 rounded"
                      disabled={isSubmitting}
                    />
                    <label htmlFor={`hub-${h.id}`} className="ml-2 text-sm text-gray-700">
                      {h.code} - {h.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Hub Addition Form */}
            {showHubForm && (
              <div className="mt-6 p-6 border rounded bg-gray-50">
                <h3 className="text-lg font-semibold mb-4">Add New Hub</h3>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    placeholder="Hub Code"
                    className="p-2 border rounded"
                    value={newHub.code}
                    onChange={e => setNewHub(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  />
                  <input
                    placeholder="Hub Name"
                    className="p-2 border rounded"
                    value={newHub.name}
                    onChange={e => setNewHub(p => ({ ...p, name: e.target.value }))}
                  />
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button type="button" className="px-4 py-2 bg-gray-500 text-white rounded" onClick={() => setShowHubForm(false)}>Cancel</button>
                  <button type="button" className="px-4 py-2 bg-green-600 text-white rounded" onClick={addHub}>Save</button>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-8">
              <button type="button" onClick={() => navigate('/Routepage')} className="px-4 py-2 bg-gray-500 text-white rounded text-sm">Cancel</button>
              <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : formData._id ? 'Update Route' : 'Add Route'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddRoute;